import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { MedChronJobActions } from './slice'
import { kMedChronGetJobsCacheDuration } from '../constants'
import { getMedChronJobsList } from '../fetch/get-med-chron-jobs-list'
import { getLatestArtifactVersion } from '../fetch/get-latest-artifact-version'
import { downloadArtifactVersion } from '@/artifacts/fetch/download-artifact-version'
import { getAuth } from 'firebase/auth'
import { trackMedChronArtifactDownload } from '@/analytics/utils/medchron-analytics'

export const medChronListenerMiddleware = createListenerMiddleware()
const startListener = medChronListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Listener: Get Med Chron Jobs (legacy reports list - download-only surface)
 */
startListener({
  actionCreator: MedChronJobActions.getMedChronJobs,
  effect: async (_action, listenerApi) => {
    // Get the existing state
    const state = listenerApi.getState().medChronJobState

    // Is loading already?
    const isLoading = state.jobsLoading

    // Cache Handling
    const cacheDuration = kMedChronGetJobsCacheDuration
    const lastFetch = state.lastFetch
    const cacheExpired = lastFetch === null || Date.now() - lastFetch > cacheDuration

    // Return early if the cache has not expired or the list is already loading
    if (!cacheExpired || isLoading) {
      return
    }

    try {
      listenerApi.dispatch(MedChronJobActions.setJobsLoading(true))
      listenerApi.dispatch(MedChronJobActions.setJobsError(false))

      const response = await getMedChronJobsList()

      // Sort by updated_at, most recent first (milliseconds since epoch)
      const sorted = [...response].sort((a, b) => {
        return b.updated_at - a.updated_at
      })

      listenerApi.dispatch(MedChronJobActions.upsertMedChronJobs(sorted))
    } catch {
      listenerApi.dispatch(MedChronJobActions.setJobsError(true))
    } finally {
      listenerApi.dispatch(MedChronJobActions.setJobsLoading(false))
      listenerApi.dispatch(MedChronJobActions.updateLastFetch())
    }
  },
})

/**
 * Listener: Get Latest Artifact Version
 * Fetches the latest artifact version for a legacy report and downloads it.
 */
startListener({
  actionCreator: MedChronJobActions.getLatestArtifactVersion,
  effect: async (action, listenerApi) => {
    const jobId = action.payload

    try {
      listenerApi.dispatch(MedChronJobActions.setLatestArtifactVersionLoading({ jobId, isLoading: true }))
      listenerApi.dispatch(MedChronJobActions.setLatestArtifactVersionError({ jobId, hasError: false }))

      const response = await getLatestArtifactVersion(jobId)

      listenerApi.dispatch(MedChronJobActions.setLatestArtifactVersion({ jobId, artifactVersion: response }))

      const userId = getAuth().currentUser?.uid
      if (!userId) {
        throw new Error('User not authenticated')
      }

      try {
        await downloadArtifactVersion(response, userId)
        trackMedChronArtifactDownload(true, response.artifact_id, response.id, jobId)
      } catch (downloadErr) {
        trackMedChronArtifactDownload(false, response.artifact_id, response.id, jobId, downloadErr instanceof Error ? downloadErr.message : 'Unknown error')
        throw downloadErr
      }
    } catch {
      listenerApi.dispatch(MedChronJobActions.setLatestArtifactVersionError({ jobId, hasError: true }))
    } finally {
      listenerApi.dispatch(MedChronJobActions.setLatestArtifactVersionLoading({ jobId, isLoading: false }))
    }
  },
})
